import React from "react";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import {Tag} from "~/components/tag/Tag.tsx";
import type {Cell, ContractDetail, ContractDocument, ContractVersion} from "../contractExplorerTypes.ts";
import type {VersionLine} from "../contractExplorerFormat.ts";
import {
    billingLabel, formatDate, formatMoney, formatNumber, formatValue, LINE_TYPE_LABEL, MISSING, priceLabel, tiersOf,
    versionLines,
} from "../contractExplorerFormat.ts";
import {Section} from "./Section.tsx";
import {PriceCell} from "./PriceCell.tsx";
import {PriceTiers} from "./PriceTiers.tsx";
import {SourceCite} from "./SourceCite.tsx";

type Props = {
    contract: ContractDetail;
    version: ContractVersion | undefined;
    documents: Record<string, ContractDocument>;
};

type Row = VersionLine & {id: string; spend: number};

const value = (cells: Record<string, Cell>, field: string): unknown => cells[field]?.value;

const CHANGE: Record<Cell['change'], {label: string; color: 'default' | 'info' | 'warning' | 'error'} | null> = {
    unchanged: null,
    changed: {label: 'Changed', color: 'warning'},
    added: {label: 'New', color: 'info'},
    removed: {label: 'Ended', color: 'error'},
};

/** A line changed when any of its values did; a removed line is removed throughout. */
function lineChange(cells: Record<string, Cell>): Cell['change'] {
    const changes = Object.values(cells).map(c => c.change);
    if (changes.includes('removed') && changes.every(c => c === 'removed')) return 'removed';
    if (changes.length && changes.every(c => c === 'added')) return 'added';
    return changes.some(c => c !== 'unchanged') ? 'changed' : 'unchanged';
}

/** The price lines of the shown version, with the spend linked to each and what changed since the version before. */
export const ContractLinesGrid: React.FC<Props> = ({contract, version, documents}) => {
    const spendByLine = React.useMemo(
        () => Object.fromEntries(contract.purchases.map(p => [p.line, p.spend])), [contract.purchases]);
    const rows = React.useMemo<Row[]>(() => versionLines(version).map(line => (
        {...line, id: line.line, spend: spendByLine[line.line] ?? 0})), [version, spendByLine]);
    const spendCurrency = contract.spend_currency;

    const columns = React.useMemo<GridColDef<Row>[]>(() => [
        {field: 'line', headerName: 'Line', width: 70},
        {
            field: 'item', headerName: 'Item', flex: 2, minWidth: 240, sortable: false,
            renderCell: ({row}) => {
                const pack = value(row.cells, 'pack_size');
                const sites = value(row.cells, 'delivery_sites');
                const sub = [value(row.cells, 'item_code'), value(row.cells, 'price_note') ?? value(row.cells, 'item_notes')]
                    .filter(Boolean).map(formatValue)
                    .concat([billingLabel(row.cells), pack ? `pack of ${formatValue(pack)}` : '',
                        Array.isArray(sites) && sites.length ? `only at ${formatValue(sites)}` : ''].filter(Boolean))
                    .join(' · ');
                const lineType = LINE_TYPE_LABEL[String(value(row.cells, 'line_type') ?? '')];
                const indexed = value(row.cells, 'indexed') === true;
                return (
                    <span className="ci-cname">
                        <b title={formatValue(value(row.cells, 'item_description'))}>
                            {formatValue(value(row.cells, 'item_description'))}
                            <SourceCite documentId={row.cells.item_description?.document_id} documents={documents}/>
                        </b>
                        {(lineType || indexed) && (
                            <span className="ci-line-tags">
                                {lineType && <Tag label={lineType} color="default" variant="outlined"/>}
                                {indexed && <Tag label="Indexed" color="default" variant="outlined"
                                                 title="The price changes with an index or a stated periodic increase"/>}
                            </span>
                        )}
                        {sub && <span className="ci-csub">{sub}</span>}
                    </span>
                );
            },
        },
        {field: 'quantity', headerName: 'Quantity', width: 100, align: 'right', headerAlign: 'right',
            valueGetter: (_v, row) => value(row.cells, 'quantity'), renderCell: ({value: v}) => formatNumber(v)},
        {field: 'unit', headerName: 'Unit', width: 120,
            valueGetter: (_v, row) => value(row.cells, 'unit_as_printed') ?? value(row.cells, 'unit'),
            renderCell: ({value: v}) => formatValue(v)},
        {field: 'unit_price', headerName: 'Price', width: 150, align: 'right', headerAlign: 'right',
            valueGetter: (_v, row) => value(row.cells, 'unit_price'),
            renderCell: ({row}) => {
                const price = priceLabel(row.cells);
                const tiers = tiersOf(row.cells, contract.documents);
                return (
                    <span className="ci-cname ci-price">
                        <PriceCell value={price.cell?.value} cell={price.cell} text={price.text}/>
                        {tiers.length > 0 && <PriceTiers tiers={tiers} documents={documents}/>}
                    </span>
                );
            }},
        {field: 'item_spend_net', headerName: 'Stated total', width: 130, align: 'right', headerAlign: 'right',
            valueGetter: (_v, row) => value(row.cells, 'item_spend_net'),
            renderCell: ({row, value: v}) => <PriceCell value={v} cell={row.cells.item_spend_net}/>},
        {field: 'currency', headerName: 'Currency', width: 100,
            valueGetter: (_v, row) => value(row.cells, 'currency'), renderCell: ({value: v}) => formatValue(v)},
        {field: 'price_mechanism', headerName: 'Pricing', width: 130,
            valueGetter: (_v, row) => value(row.cells, 'price_mechanism'), renderCell: ({value: v}) => formatValue(v)},
        {field: 'valid', headerName: 'Price valid', width: 190, sortable: false,
            renderCell: ({row}) => {
                const from = value(row.cells, 'price_valid_from') as string | null;
                const to = value(row.cells, 'price_valid_to') as string | null;
                return from || to ? `${formatDate(from)} → ${to ? formatDate(to) : 'open'}` : MISSING;
            }},
        {field: 'spend', headerName: spendCurrency ? `Spend under contract (${spendCurrency})` : 'Spend under contract',
            width: 190, align: 'right', headerAlign: 'right', renderCell: ({value: v}) => formatMoney(v as number)},
        {field: 'change', headerName: 'Since last version', width: 150, sortable: false,
            renderCell: ({row}) => {
                const change = CHANGE[lineChange(row.cells)];
                return change ? <Tag label={change.label} color={change.color}/> : <span className="ci-dash">—</span>;
            }},
    ], [contract.documents, documents, spendCurrency]);

    return (
        <Section title={`Contract lines (${rows.length})`}
                 sub={rows.length ? 'Prices as the documents state them; spend under contract is what linkage linked to each line.' : undefined}>
            {rows.length ? (
                <div className="ci-grid">
                    <CustomDataGrid
                        rows={rows}
                        columns={columns}
                        autoHeight
                        autoRowHeight
                        hideFooter={rows.length <= 25}
                        pagination
                        pageSizeOptions={[25]}
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                        disableRowSelectionOnClick
                        getRowClassName={({row}) => (lineChange(row.cells) === 'removed' ? 'ci-row-removed' : '')}
                    />
                </div>
            ) : (
                <div className="ci-empty">
                    <h3 className="ci-empty-h">No contract line was extracted</h3>
                    <p className="ci-empty-p">No document of this bundle carries a price line, so spend can only be linked to the bundle as a whole.</p>
                </div>
            )}
        </Section>
    );
};
